import { montajDosyaAilesi, montajRnBul, raporDosyaBul, type MontajListeSatiri, type MontajTopluYaniti } from "@oasis/domain";
import { cevir } from "@/lib/dil";
import { AG_HATASI, dosyaTuru, sirali, type DosyaBenzeri, type Ton } from "@/lib/mrb-toplu";

/**
 * D-068: "Upload Assembly reports" penceresinin saf mantığı (söküm penceresindeki `sokum-toplu.ts`'in kopyası; o dosya canlı
 * ekranındır, bilerek ortaklaştırılmadı: emsal `rapor-toplu.ts`). Ekran (MontajTopluYukleme.tsx) yalnız çizer; eşleme, satır
 * durumları, sunucu yanıtının satır sonucuna çevrilmesi, sıradaki satırın seçimi ve gösterilecek metinler burada. JSX yok; React
 * ve oturum katmanını yüklemez.
 * Söküm penceresinden bilinçli farklar: riser ve dosya ailesi alan paketindeki montaj kuralıyla okunur (`montajRnBul`,
 * `montajDosyaAilesi`); adı söküm raporu ya da Disposition / Rework raporu diyen dosya satır olur ama GÖNDERİLMEZ; çok parçalı
 * alan adları `pdf` ve `excel`'dir; MRB eşleşmesi ve teslim alma notu yoktur (montaj verisi MRB bağı kurmaz, teslim almaya
 * dokunmaz). Bu pencereden açılan her yeni kayıt arşiv kaydıdır: sunucu Finishing kuyruğundaki riser için kayıt açmaz ("akis").
 */

/**
 * bekliyor: gönderilecek · yukleniyor: istek sürüyor · tamam: sunucu yanıtladı (`sonuc` dolu) ·
 * rn_gerekli: riser okunamadı, satırda seçilir · hata: sunucu ya da ağ hatası, ya da başka bir pencerenin dosyası (gönderilmez).
 */
export type SatirDurumu = "bekliyor" | "yukleniyor" | "tamam" | "rn_gerekli" | "hata";

/** `POST /api/montaj/toplu` yanıtı (200): alan paketindeki `MontajTopluYaniti` ile aynı tip. */
export type TopluYaniti = MontajTopluYaniti;

export interface SatirHatasi {
  /** Sunucunun İngilizce metni (gösterimde çevrilir) ya da bu modülün metni. */
  metin: string;
  /**
   * "rn": riser okunamadı · "excel": assembly girdisi reddedildi (PDF de kaydedilmedi) · "akis": riser Finishing kuyruğunda, yeni
   * kayıt bu pencereden açılmaz · "tur": dosya başka bir pencerenindir, gönderilmedi · "oturum": oturum bitti (401), tur durdu.
   */
  kaynak: "rn" | "excel" | "akis" | "tur" | "oturum" | null;
  /** Ağ hatası, sunucu arızası (5xx), biten oturum (401), zaman aşımı (408) ya da hız sınırı (429): aynı satır olduğu gibi yeniden denenebilir. */
  tekrarlanabilir: boolean;
}

export interface MontajSatiri<D extends DosyaBenzeri = File> {
  id: number;
  pdf: D | null;
  excel: D | null;
  /** Dosya adından okunan riser (eşleme bununla yapılır). */
  rn: string | null;
  /** Kullanıcının satırda seçtiği riser; sunucuya yalnız bu gider (dosya adındakini sunucu kendisi okur). */
  secilenRn: string | null;
  durum: SatirDurumu;
  sonuc: TopluYaniti | null;
  hata: SatirHatasi | null;
}

/** Pencerenin gönderdiği uç (`dosyaYukle` `/api` önekini kendisi ekler). */
export const TOPLU_UCU = "/montaj/toplu";
/** Çok parçalı alan adları: Assembly raporu PDF'i ve assembly girdi excel'i. */
export const PDF_ALANI = "pdf";
export const EXCEL_ALANI = "excel";

/** Adı söküm raporu (ya da söküm girdisi) diyen dosya: satır gönderilmez. */
export const SOKUM_DOSYASI = "This looks like a Disassembly report. Upload it with Upload Disassembly reports on the Disassembly page.";
/** Adı Disposition ya da Rework raporu (ya da girdisi) diyen dosya: satır gönderilmez. */
export const RAPOR_DOSYASI = "This looks like a Disposition or Rework report. It is not uploaded from this window.";

/** Oturum bitti (401): satır ve kalan bekleyenler gönderilmedi. */
const OTURUM_BITTI = "Your session has ended, so this row and the waiting rows were not sent. Sign in again in another tab, then retry this row and press Upload.";

/** "Assembly report R106": dönüştürücü kutusunda ve satır cümlesinde raporun adı. */
export const raporAdi = (rn: string): string => cevir("Assembly report {0}", rn);

/** Girdi bağlanan satırın etiketi ve pencerenin sayaç başlığı. */
export const girdiBaglandiAdi = (): string => cevir("assembly input attached");

/** Satırın riseri: satırda seçilen, yoksa dosya adındaki. */
export const etkinRn = (s: Pick<MontajSatiri<DosyaBenzeri>, "rn" | "secilenRn">): string | null => s.secilenRn ?? s.rn;

function yeniSatir<D extends DosyaBenzeri>(id: number, pdf: D | null, excel: D | null, rn: string | null): MontajSatiri<D> {
  return { id, pdf, excel, rn, secilenRn: null, durum: rn ? "bekliyor" : "rn_gerekli", sonuc: null, hata: null };
}

/** PDF'ini bekleyen tek başına excel satırı: henüz gönderilmemiş ya da sunucudan "needs its PDF" almış. */
const pdfBekleyenExcel = (s: MontajSatiri<DosyaBenzeri>) => !s.pdf && Boolean(s.excel) && (s.durum === "bekliyor" || (s.durum === "tamam" && s.sonuc?.sonuc === "pdf_gerekli"));

/** Dosya adı başka bir pencereyi söylüyorsa o pencerenin cümlesi: önce söküm ailesi, sonra Disposition / Rework türü. */
function baskaPencere(ad: string): string | null {
  if (montajDosyaAilesi(ad) === "disassembly") return SOKUM_DOSYASI;
  return raporDosyaBul(ad).tur ? RAPOR_DOSYASI : null;
}

/**
 * Seçilen ya da sürüklenen dosyaları satırlara ekler. Önce PDF'ler, sonra excel'ler işlenir ki aynı seçimde sıra fark etmesin.
 * - Aile denetimi (yalnız bu pencerede): adı söküm raporu diyen dosya (`montajDosyaAilesi` "disassembly") kendi satırında "error"
 *   olur ve gönderilmez, hiçbir satırla eşlenmez; adı Disposition ya da Rework raporu diyen dosya (`raporDosyaBul`) da öyle.
 *   Ailesi belirsiz ad ("RN 108.PDF") bu pencerenin sayılır.
 * - Riser `montajRnBul` ile okunur (PDF adları ve "R106-assembly-input-EN.xlsx"); okunamazsa satır "rn_gerekli" olur.
 * - PDF: aynı riserda PDF'ini bekleyen tek başına excel satırı varsa ona girer ("needs its PDF" almış satır yeniden bekler);
 *   yoksa yeni satır. Aynı riserın ikinci PDF'i ayrı satır olur ve sunucudan "not changed" alır.
 * - Excel: aynı riserda excel'i olmayan, henüz gönderilmemiş PDF satırına girer; yoksa yeni satır. Tek başına excel de gönderilir.
 * PDF ve .xlsx dışındaki dosyalar eklenmez, adları `atlanan`da döner.
 */
export function dosyalariEkle<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[], dosyalar: readonly D[]): { satirlar: MontajSatiri<D>[]; atlanan: string[] } {
  const sonuc = satirlar.map((s) => ({ ...s }));
  let id = sonuc.reduce((m, s) => Math.max(m, s.id), 0);
  const atlanan = dosyalar.filter((f) => !dosyaTuru(f.name)).map((f) => f.name);
  /** Başka bir pencerenin dosyası: satırı açılır, gönderilmez. Riser yalnız gösterim içindir. */
  const yanlisAile = (f: D): boolean => {
    const metin = baskaPencere(f.name);
    if (!metin) return false;
    const pdf = dosyaTuru(f.name) === "pdf";
    const rn = montajRnBul(f.name) ?? raporDosyaBul(f.name).rn;
    sonuc.push({ ...yeniSatir(++id, pdf ? f : null, pdf ? null : f, rn), durum: "hata", hata: { metin, kaynak: "tur", tekrarlanabilir: false } });
    return true;
  };

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "pdf")) {
    if (yanlisAile(f)) continue;
    const rn = montajRnBul(f.name);
    const bekleyen = rn ? sonuc.find((s) => pdfBekleyenExcel(s) && etkinRn(s) === rn) : undefined;
    if (bekleyen) {
      bekleyen.pdf = f;
      bekleyen.durum = "bekliyor";
      bekleyen.sonuc = null;
      continue;
    }
    sonuc.push(yeniSatir(++id, f, null, rn));
  }

  for (const f of dosyalar.filter((x) => dosyaTuru(x.name) === "excel")) {
    if (yanlisAile(f)) continue;
    const rn = montajRnBul(f.name);
    const pdfSatiri = rn ? sonuc.find((s) => s.durum === "bekliyor" && s.pdf && !s.excel && etkinRn(s) === rn) : undefined;
    if (pdfSatiri) {
      pdfSatiri.excel = f;
      continue;
    }
    sonuc.push(yeniSatir(++id, null, f, rn));
  }
  return { satirlar: sonuc, atlanan };
}

/**
 * Riseri okunamayan satırda seçilen riser ("Retry"): satır yeniden gönderilmek üzere bekler. Aynı riserın öteki yarısı ayrı
 * satırda gönderilmeyi bekliyorsa bu satıra taşınır, o satır kalkar: satırın excel'i yoksa PDF'ini bekleyen tek başına excel,
 * satırın PDF'i yoksa excel'i olmayan bekleyen PDF. Böylece ikisi tek istekte gider. Başka pencerenin dosyasına dokunulmaz.
 */
export function rnSec<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[], id: number, rn: string): MontajSatiri<D>[] {
  const hedef = satirlar.find((s) => s.id === id);
  if (!hedef || !rn || (!hedef.pdf && !hedef.excel) || hedef.hata?.kaynak === "tur") return [...satirlar];
  const oteki = satirlar.find((s) => s.id !== id && etkinRn(s) === rn && (
    (hedef.pdf && !hedef.excel && pdfBekleyenExcel(s))
    || (!hedef.pdf && hedef.excel && s.durum === "bekliyor" && Boolean(s.pdf) && !s.excel)
  ));
  return satirlar
    .filter((s) => s !== oteki)
    .map((s) => (s.id === id ? { ...s, secilenRn: rn, pdf: s.pdf ?? oteki?.pdf ?? null, excel: s.excel ?? oteki?.excel ?? null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Satırı olduğu gibi yeniden kuyruğa koyar ("Retry": ağ hatası, sunucu arızası, biten oturum). Başka pencerenin dosyası yeniden denenmez. */
export function tekrarDene<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[], id: number): MontajSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && (s.pdf || s.excel) && s.hata?.kaynak !== "tur" ? { ...s, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/** Excel'i reddedilen satır: excel satırdan çıkar, PDF tek başına yeniden gönderilir ("Retry without excel"). */
export function excelsizDene<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[], id: number): MontajSatiri<D>[] {
  return satirlar.map((s) => (s.id === id && s.pdf && s.hata?.kaynak !== "tur" ? { ...s, excel: null, durum: "bekliyor" as const, sonuc: null, hata: null } : s));
}

/**
 * Gönderilecek sıradaki satır: listede en üstteki bekleyen (riseri belli, en az bir dosyası olan). `secim` null ise ("Upload" turu)
 * bütün bekleyenler; küme ise yalnız içindeki satırlar (satır düğmesiyle yeniden denenenler). `secimeEkle` MRB penceresiyle ortaktır.
 */
export function siradaki<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[], secim: ReadonlySet<number> | null = null): MontajSatiri<D> | null {
  return satirlar.find((s) => s.durum === "bekliyor" && (s.pdf || s.excel) && etkinRn(s) && (!secim || secim.has(s.id))) ?? null;
}

/**
 * Satırın isteği: `dosyaYukle(TOPLU_UCU, dosya, alanlar, ekDosyalar, dosyaAlan)`. PDF varsa ana dosya odur ("pdf"), excel yanında
 * "excel" alanında gider; tek başına excel "excel" alanında gider. Riser yalnız satırda seçildiyse gönderilir.
 */
export function istekParcalari<D extends DosyaBenzeri>(s: MontajSatiri<D>): { dosya: D; dosyaAlan: typeof PDF_ALANI | typeof EXCEL_ALANI; alanlar: Record<string, string>; ekDosyalar: Record<string, D> } {
  const alanlar: Record<string, string> = s.secilenRn ? { rn: s.secilenRn } : {};
  if (s.pdf) return { dosya: s.pdf, dosyaAlan: PDF_ALANI, alanlar, ekDosyalar: s.excel ? { [EXCEL_ALANI]: s.excel } : {} };
  if (s.excel) return { dosya: s.excel, dosyaAlan: EXCEL_ALANI, alanlar, ekDosyalar: {} };
  throw new Error("Dosya yok: bu satır gönderilmez.");
}

/** Gönderimin ham sonucu: 2xx gövdesi · 4xx/5xx (gövdesiyle) · ağ hatası. */
export type Gonderim =
  | { tur: "yanit"; govde: TopluYaniti }
  | { tur: "hata"; status: number; govde: unknown; mesaj: string }
  | { tur: "ag" };

type SatirSonucu = Pick<MontajSatiri, "durum" | "sonuc" | "hata">;

function hataSonucu(metin: string, kaynak: unknown, tekrarlanabilir: boolean): SatirSonucu {
  const k = kaynak === "rn" || kaynak === "excel" || kaynak === "akis" ? kaynak : null;
  return { durum: k === "rn" ? "rn_gerekli" : "hata", sonuc: null, hata: { metin, kaynak: k, tekrarlanabilir } };
}

/**
 * Yanıt turu durdurur mu: oturum bitti (401) ya da bu işleme yetki yok (403). Kalan her satır aynı yanıtı alırdı; dosyaları
 * boşuna gönderilmez, "waiting" kalırlar.
 */
export const turuDurdurur = (g: Gonderim): boolean => g.tur === "hata" && (g.status === 401 || g.status === 403);

/**
 * Sunucu yanıtını satır sonucuna çevirir. Hata 4xx gövdesinde `{hata, hataKaynagi}` ile gelir: "rn" → satırda riser seçimi;
 * "excel" → assembly girdisi reddedildi ("Retry without excel"); "akis" → riser Finishing kuyruğunda (yeniden denenmez); ağ
 * hatası, 5xx, 408 ve 429 → "Retry". 401 (oturum bitti) kendi cümlesiyle "Retry" alır; 403 sunucunun yetki cümlesini gösterir,
 * düğmesi yoktur.
 */
export function sonucuIsle(g: Gonderim): SatirSonucu {
  if (g.tur === "ag") return hataSonucu(AG_HATASI, null, true);
  if (g.tur === "hata") {
    if (g.status === 401) return { durum: "hata", sonuc: null, hata: { metin: OTURUM_BITTI, kaynak: "oturum", tekrarlanabilir: true } };
    const govde = (g.govde && typeof g.govde === "object" ? g.govde : {}) as { hata?: unknown; message?: unknown; hataKaynagi?: unknown };
    const metin = typeof govde.hata === "string" ? govde.hata : typeof govde.message === "string" ? govde.message : g.mesaj;
    return hataSonucu(metin, govde.hataKaynagi, g.status >= 500 || g.status === 408 || g.status === 429);
  }
  return { durum: "tamam", sonuc: g.govde, hata: null };
}

export interface SatirGorunumu {
  etiketler: { metin: string; ton: Ton }[];
  aciklama: { metin: string; ton: "ink-2" | "bad" } | null;
  /** Açıklamanın altındaki satırlar: sunucunun uyarıları (sarı). */
  notlar: { metin: string; ton: "ink-2" | "warn" | "ink-3" }[];
  /** RN hücresinde riser seçimi ve "Retry". */
  rnSecimi: boolean;
  dugme: "tekrar" | "excelsiz" | null;
}

/** Hata satırı: başka pencerenin dosyası, reddedilen girdi, kuyruktaki riser, sunucu ve ağ hataları. */
function hataGorunumu<D extends DosyaBenzeri>(satir: MontajSatiri<D>, bos: SatirGorunumu): SatirGorunumu {
  const h = satir.hata;
  const etiketler: SatirGorunumu["etiketler"] = [{ metin: cevir("error"), ton: "bad" }];
  if (!h) return { ...bos, etiketler, aciklama: { metin: cevir("Upload failed"), ton: "bad" } };
  // Reddedilen girdi PDF'le eşliyse PDF de kaydedilmemiştir: satır excel'siz yeniden denenebilir.
  if (h.kaynak === "excel" && satir.pdf) return { ...bos, etiketler, aciklama: { metin: cevir("Assembly input rejected: {0} The PDF was not saved either.", cevir(h.metin)), ton: "bad" }, dugme: "excelsiz" };
  return { ...bos, etiketler, aciklama: { metin: cevir(h.metin), ton: "bad" }, dugme: h.tekrarlanabilir ? "tekrar" : null };
}

/** Sunucunun yanıtladığı satır. `satir`: açıklama satırdaki dosyalara da bakar (PDF'i var mı). */
function sonucGorunumu<D extends DosyaBenzeri>(satir: MontajSatiri<D>, s: TopluYaniti, bos: SatirGorunumu): SatirGorunumu {
  const notlar: SatirGorunumu["notlar"] = s.uyarilar.map((u) => ({ metin: cevir(u), ton: "warn" }));
  const o = s.ozet ?? { satir: 0, birim: 0, celiski: 0, eksikSayfa: 0 };
  switch (s.sonuc) {
    case "degismedi":
      return { ...bos, etiketler: [{ metin: cevir("not changed"), ton: "warn" }], aciklama: { metin: cevir("{0} already has an assembly record; nothing from this row was saved.", s.rn), ton: "ink-2" } };
    case "pdf_gerekli":
      return { ...bos, etiketler: [{ metin: cevir("needs its PDF"), ton: "warn" }], aciklama: { metin: cevir("{0} has no assembly record, so an excel alone is not sent from here. Add its PDF to this window.", s.rn), ton: "ink-2" } };
    case "girdi_baglandi": {
      const cumleler: (string | null)[] = s.tamamlandi
        ? [cevir("The assembly record of {0} was waiting for its assembly input, so this excel was read into it: {1} rows · {2} units · {3} conflicts. Record completed.", s.rn, o.satir, o.birim, o.celiski)]
        : [
            cevir("The assembly record of {0} had no assembly input, so this excel was read into it: {1} rows · {2} units · {3} conflicts.", s.rn, o.satir, o.birim, o.celiski),
            s.pdfKaydedilmedi ? cevir("The PDF on the record was kept; the PDF in this row was not saved.") : !satir.pdf ? cevir("The PDF on the record was kept.") : null,
          ];
      if (s.siradanCikti) cumleler.push(cevir("The record left the converter queue."));
      return { ...bos, etiketler: [{ metin: girdiBaglandiAdi(), ton: "ok" }], aciklama: { metin: cumleler.filter(Boolean).join(" "), ton: "ink-2" }, notlar };
    }
    case "yuklendi": {
      const etiketler: SatirGorunumu["etiketler"] = [{ metin: cevir("uploaded"), ton: "ok" }];
      // Yalnız PDF: kayıt bekleyen açıldı. Sıra numarası yoksa (iş yanıt kurulurken sıradan çıktı) "kuyruğa eklendi" denmez.
      if (s.kuyrugaGirdi || !s.ozet) {
        const metin = s.siraNo
          ? cevir("No assembly input: added to the converter queue ({0}). The assembly record of {1} is open and waits for the excel.", sirali(s.siraNo), s.rn)
          : cevir("{0} is uploaded and has no assembly input yet. Its converter state is on the Assembly records list.", raporAdi(s.rn));
        return { ...bos, etiketler, aciklama: { metin, ton: "ink-2" }, notlar };
      }
      const okunan: (string | null)[] = [
        cevir("Assembly input read: {0} rows · {1} units · {2} conflicts. Assembly record of {3} completed. Not queued.", o.satir, o.birim, o.celiski, s.rn),
        o.eksikSayfa > 0 ? cevir("{0} page(s) of the form are not in the scanned PDF.", o.eksikSayfa) : null,
      ];
      return { ...bos, etiketler, aciklama: { metin: okunan.filter(Boolean).join(" "), ton: "ink-2" }, notlar };
    }
  }
}

/** Satırın "Result" hücresi (tasarım: assembly--toplu-yukleme). */
export function satirGorunumu<D extends DosyaBenzeri>(satir: MontajSatiri<D>): SatirGorunumu {
  const bos: SatirGorunumu = { etiketler: [], aciklama: null, notlar: [], rnSecimi: false, dugme: null };
  switch (satir.durum) {
    case "bekliyor":
      return { ...bos, etiketler: [{ metin: cevir("waiting"), ton: "neutral" }] };
    case "yukleniyor":
      return { ...bos, etiketler: [{ metin: cevir("uploading…"), ton: "info" }] };
    case "rn_gerekli":
      return {
        ...bos,
        etiketler: [{ metin: cevir("riser could not be read"), ton: "warn" }],
        aciklama: { metin: satir.hata ? cevir(satir.hata.metin) : cevir("The file name has no riser number. Select the riser and retry this row."), ton: "ink-2" },
        rnSecimi: true,
      };
    case "hata":
      return hataGorunumu(satir, bos);
    case "tamam":
      return satir.sonuc ? sonucGorunumu(satir, satir.sonuc, bos) : { ...bos, etiketler: [{ metin: cevir("uploaded"), ton: "ok" }] };
  }
}

export interface TopluOzet {
  toplam: number;
  /** Sonucu belli satırlar (sırada ve yüklenmekte olanlar dışındakiler). */
  biten: number;
  yuklendi: number;
  girdiBaglandi: number;
  degismedi: number;
  rnGerekli: number;
  pdfGerekli: number;
  hata: number;
  yukleniyor: number;
  bekliyor: number;
}

/** Pencerenin ilerleme satırı: "12 of 15 done" ve durum sayaçları. Başka pencerenin dosyası "error" sayılır. */
export function topluOzet<D extends DosyaBenzeri>(satirlar: readonly MontajSatiri<D>[]): TopluOzet {
  const o: TopluOzet = { toplam: satirlar.length, biten: 0, yuklendi: 0, girdiBaglandi: 0, degismedi: 0, rnGerekli: 0, pdfGerekli: 0, hata: 0, yukleniyor: 0, bekliyor: 0 };
  for (const s of satirlar) {
    if (s.durum === "bekliyor") o.bekliyor++;
    else if (s.durum === "yukleniyor") o.yukleniyor++;
    else if (s.durum === "rn_gerekli") o.rnGerekli++;
    else if (s.durum === "hata") o.hata++;
    else if (s.sonuc?.sonuc === "girdi_baglandi") o.girdiBaglandi++;
    else if (s.sonuc?.sonuc === "degismedi") o.degismedi++;
    else if (s.sonuc?.sonuc === "pdf_gerekli") o.pdfGerekli++;
    else o.yuklendi++;
  }
  o.biten = o.toplam - o.bekliyor - o.yukleniyor;
  return o;
}

/**
 * Pencerenin RN seçicisindeki riserlar, sıralı ve tekrarsız. Sistemdeki riserlardan (`MontajListesi.riserler`) şunlar çıkarılır:
 * - kaydı okunmuş olan ya da ekranda doldurulan form kaydı olanlar (onlara bu pencereden bir şey yüklenemez);
 * - Finishing kuyruğunda olup kaydı OLMAYANLAR (`kuyruktakiler`: `GET /api/assembly` yanıtındaki `risers`, yani "Send to
 *   assembly" ile gönderilmiş riserlar; `muayeneden` listesi verilmez). Sunucu onlara bu uçtan kayıt açmaz.
 * Kuyruktaki riserın bekleyen ya da girdisiz kaydı listede kalır: ona excel bağlanabilir.
 */
export function secilebilirRnler(riserler: readonly string[], kayitlar: readonly Pick<MontajListeSatiri, "rn" | "tur" | "hal">[], kuyruktakiler: readonly string[]): string[] {
  const kaydi = new Map(kayitlar.map((k) => [k.rn, k]));
  const kuyrukta = new Set(kuyruktakiler);
  return [...new Set(riserler)]
    .filter((rn) => {
      const k = kaydi.get(rn);
      return k ? k.tur === "rapor" && k.hal !== "okundu" : !kuyrukta.has(rn);
    })
    .sort();
}
